import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Container from "../../../../components/structure/Container";
import Breadcrumbs from "../../../../components/ui/Breadcrumbs";

import PropertyBooker from "../../../../components/ui/Booking/PropertyBookerLandingPage/PropertyBooker";
import Head from "../../../../components/ui/Head/Head";
import ImageGallery from "../../../../components/ui/ImageGallery";
import ImageMosaicShow from "../../../../components/ui/ImageMosaicShow";
import { Loading } from "../../../../components/ui/Loading";
import { Experience } from "../../../../definitions/experience";
import { Property } from "../../../../definitions/property";
import { useUnauthenticatedApi } from "../../../../hooks/useApi";
import useBookingService from "../../../../hooks/useBookingService";
import useWebview from "../../../../hooks/useWebview";
import { smoothScrollTo } from "../../../../utils/animate";
import Page from "../../components/Page";
import ExperienceGrid from "../Explore/components/ExperienceGrid";
import PropertyGrid from "../Property/components/PropertyGrid";

declare global {
  interface Window {
    reactLoaded: any;
  }
}

interface PropertyLandingPageProps {}

interface PageParams {
  propertyCode: string | undefined;
}

const PropertyLandingPage: React.FC<PropertyLandingPageProps> = () => {
  const { propertyCode } = useParams<PageParams>();

  const { inWebview } = useWebview();
  const bookingService = useBookingService();
  const hostelDataApi = useUnauthenticatedApi("STAY_OPERATORS");
  const destinationApi = useUnauthenticatedApi("STAY_DESTINATIONS");
  const otherStayElement = useRef<HTMLElement>(null);
  const experienceByDestinationApi = useUnauthenticatedApi(
    "DISCOVER_DESTINATION"
  );
  const [galleryVisible, setGalleryVisible] = useState<boolean>(false);
  const [galleryData, setGalleryData] = useState<any[]>();

  const [propertyInfo, setPropertyInfo] = useState<Property | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [experience, setExperience] = useState<Experience[]>([]);
  const [moreProperties, setMoreProperties] = useState<Property[]>([]);

  const selectedRoomId: number | null = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const roomParam = urlParams.get("room");
    const roomsParam = urlParams.get("rooms");

    if (roomParam) {
      const getRoomId = roomParam.split("-");
      if (getRoomId.length > 1) {
        return parseInt(getRoomId[1], 10);
      }
    }

    if (roomsParam) {
      const getRoomId = roomsParam.split("-");
      if (getRoomId.length > 0) {
        const parsedId = parseInt(getRoomId[0], 10);
        return isNaN(parsedId) ? null : parsedId;
      }
    }

    return null;
  }, []);

  const guestCount = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const guests = urlParams.get("guests");
    return guests ? parseInt(guests, 10) : 1;
  }, []);

  useEffect(() => {
    if (destinationApi && propertyInfo)
      destinationApi.get(`/${propertyInfo.destination?.slug}/`).then((res) => {
        setMoreProperties(
          res.data.destination.operators.filter(
            (property: Property) =>
              property.slug !== propertyCode && property.operating_model !== "T"
          )
        );
      });
  }, [destinationApi, propertyInfo, propertyCode]);

  useEffect(() => {
    setLoading(true);
    hostelDataApi
      .get(`/${propertyCode}/`)
      .then((response) => {
        setLoading(false);
        if ("reactLoaded" in window) {
          window.reactLoaded();
        }
        // console.log("Property Information:", response.data.operator);
        let operatorData: Property = response.data.operator;
        operatorData = bookingService.sortPropertyImages(operatorData);
        setPropertyInfo(operatorData);
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyCode]);

  const breadcrumbs = useMemo(
    () =>
      propertyInfo && propertyInfo.destination
        ? [
            { text: "Home", to: "/" },
            {
              text: propertyInfo.name,
              to: `/${propertyCode}`,
            },
          ]
        : [],
    [propertyInfo, propertyCode]
  );

  useEffect(() => {
    if (experienceByDestinationApi && propertyInfo)
      experienceByDestinationApi
        .get(`/${propertyInfo.destination?.code}/experiences/`)
        .then((res) => {
          setExperience(res.data.experiences);
        });
  }, [experienceByDestinationApi, propertyInfo]);

  const galleryImages = propertyInfo?.rooms.map((room) => {
    const images = room.images.map((image) => image.image);
    return { title: room.name, images };
  });

  galleryImages?.unshift({
    title: "Property",
    images: (propertyInfo?.images ?? []).map((image) => image.image),
  });

  const handleClose = () => {
    setGalleryVisible(false);
    if (galleryData) {
      setGalleryData([]);
    }
  };
  const scrollToOtherStays = () => {
    smoothScrollTo(otherStayElement.current, 1000);
  };
  return galleryVisible && propertyInfo ? (
    <Container className="px-4 pb-12 sm:px-0">
      <ImageGallery
        images={
          galleryData && galleryData?.length > 0 ? galleryData : galleryImages
        }
        close={handleClose}
      />
    </Container>
  ) : (
    <Page isStickyHeader={false} isStickyHeaderMobile={false}>
      {loading ? (
        <section
          className="flex flex-col items-center justify-center w-full h-144 bg-blur"
          style={{ backgroundColor: inWebview ? "#191514" : "" }}
        >
          <Loading
            className="p-4 rounded-full shadow-lg w-18 h-18"
            style={{ backgroundColor: inWebview ? "#2e2928" : "white" }}
          />

          <span
            className="mt-4 italic font-medium text-text"
            style={{ color: inWebview ? "#f0ece0" : "" }}
          >
            "Not all those who wander are lost."
          </span>
        </section>
      ) : propertyInfo != null ? (
        <section className="-mt-4 sm:mt-4">
          <Head
            title={propertyInfo.title}
            description={
              propertyInfo.short_description ||
              (propertyInfo.tagline &&
                propertyInfo.name + " " + propertyInfo.tagline)
            }
            image={
              propertyInfo.images[0].image
                ? `${propertyInfo.images[0].image}?w=600`
                : ""
            }
          />
          <Container className="my-4 h-96 sm:h-120 px-4 md:px-8 sm:block">
            {propertyInfo.images != null && propertyInfo.images.length > 0 && (
              <ImageMosaicShow
                images={propertyInfo.images}
                visibleCount={Math.min(
                  propertyInfo.images.length,
                  window.innerWidth > 640 ? 4 : 3
                )}
                setGalleryVisible={setGalleryVisible.bind(null, true)}
              />
            )}
          </Container>

          <Container className="px-6 sm:px-8">
            <Breadcrumbs links={breadcrumbs} />
          </Container>
          <PropertyBooker
            initialRoomsInfo={propertyInfo.rooms}
            propertyCode={propertyInfo.code}
            bookableMonths={propertyInfo.bookable_months}
            minBookingDate={propertyInfo.min_booking_date}
            bookableOccupancy={propertyInfo.bookable_occupancy}
            bookingDelay={propertyInfo.booking_delay}
            defaultNights={1}
            minNights={propertyInfo.booking_min_length}
            setGalleryVisible={setGalleryVisible.bind(null, true)}
            setGalleryData={setGalleryData}
            propertyInfo={propertyInfo}
            scrollToOtherStays={scrollToOtherStays}
            moreProperties={moreProperties && moreProperties}
            selectedRoomId={selectedRoomId ?? null}
            guestCount={guestCount}
          />

          {moreProperties &&
            moreProperties.length > 0 &&
            window.innerWidth < 768 && (
              <Container
                className="px-4 py-6 mt-10"
                containerClassName="bg-accent"
              >
                <section ref={otherStayElement}>
                  <h2 className="text-xl font-medium sm:text-3xl">
                    Other<span className="text-orange"> Stays</span> near{" "}
                    {propertyInfo.destination?.name}
                  </h2>
                  <PropertyGrid
                    data={moreProperties}
                    slug={
                      propertyInfo.destination?.slug
                        ? propertyInfo.destination.slug
                        : ""
                    }
                  />
                </section>
              </Container>
            )}
          {experience && experience.length > 0 && (
            <Container
              className="px-4 py-6"
              containerClassName={`${
                moreProperties && moreProperties.length > 0 ? "" : "bg-accent"
              }`}
            >
              <h2 className="text-xl font-medium sm:text-3xl">
                Explore
                <span className="text-orange"> Trips</span> Around{" "}
                {propertyInfo.name}
              </h2>
              <ExperienceGrid data={experience} />
            </Container>
          )}
        </section>
      ) : null}
    </Page>
  );
};

export default PropertyLandingPage;
